import { CheckIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@langchain/untitled-ui-icons';
import { PopoverPortal } from '@radix-ui/react-popover';

import { useEffect, useState } from 'react';

import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/Command';
import { DisabledTooltip } from '@/components/DisabledTooltip';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { TextOverflowTooltip } from '@/components/TextOverflowTooltip';
import { VariableInputForm } from '@/components/VariableInputForm/VariableInputForm';
import { RunStatsSchema } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { FilterComp, StandardFilterField } from '../constants';

type BasicValueType = string | number | boolean;

export function CustomValue(props: {
  value: BasicValueType | null;
  onChange: (value: BasicValueType | null) => void;
  valueType?: 'string' | 'number' | 'boolean';
}) {
  const strValue =
    typeof props.value === 'string' ? props.value : JSON.stringify(props.value);

  const numericValue =
    props.value != null && !Number.isNaN(+props.value) ? +props.value : null;

  const itemStyle = 'flex items-center gap-2 px-2 py-1 text-xs';
  if (numericValue != null) {
    return (
      <>
        <CommandItem
          key={strValue}
          className={itemStyle}
          onSelect={() => props.onChange(props.value)}
        >
          <TextOverflowTooltip>"{strValue}"</TextOverflowTooltip>
        </CommandItem>

        <CommandItem
          key={`${strValue}-numberic`}
          className={itemStyle}
          onSelect={() => props.onChange(numericValue)}
        >
          <TextOverflowTooltip>{strValue}</TextOverflowTooltip>
        </CommandItem>
      </>
    );
  }

  if (props.value === 'null') {
    return (
      <>
        <CommandItem
          className={itemStyle}
          onSelect={() => props.onChange(props.value)}
        >
          <span>"null"</span>
        </CommandItem>

        <CommandItem
          className={itemStyle}
          onSelect={() => props.onChange(null)}
        >
          <span>null</span>
        </CommandItem>
      </>
    );
  }

  if (props.value === 'true' || props.value === 'false') {
    return (
      <>
        <CommandItem
          className={itemStyle}
          onSelect={() => props.onChange(props.value)}
        >
          <TextOverflowTooltip>"{strValue}"</TextOverflowTooltip>
        </CommandItem>

        <CommandItem
          className={itemStyle}
          onSelect={() => props.onChange(props.value === 'true')}
        >
          <TextOverflowTooltip>{strValue}</TextOverflowTooltip>
        </CommandItem>
      </>
    );
  }

  return (
    <CommandItem
      className={itemStyle}
      onSelect={() => props.onChange(props.value)}
    >
      <TextOverflowTooltip>{strValue}</TextOverflowTooltip>
    </CommandItem>
  );
}

export function FilterBarArgumentInput(props: {
  stats: RunStatsSchema | undefined;
  field: StandardFilterField;
  comparator: FilterComp | undefined;
  value: BasicValueType | null | Array<BasicValueType> | undefined;
  onChange: (
    value: BasicValueType | null | Array<BasicValueType> | undefined
  ) => void;
  getFieldFacets?: (value: {
    runStats: RunStatsSchema | undefined;
  }) => Array<{ value: BasicValueType }>;
  updateEveryChange?: boolean;
  readOnly?: boolean;
}) {
  const [open, setOpen] = useState(false);

  const isReadOnly = props.readOnly ?? false;

  const facets = props.stats?.run_facets ?? [];

  let fieldFacets: Array<{ value: BasicValueType }> = facets.filter(
    (i) => i.key === props.field.id
  );

  const valueType =
    props.comparator?.valueType || props.field.valueType || 'string';
  const isMultiple = props.comparator?.multiple ?? false;

  if (valueType === 'boolean') {
    fieldFacets = [{ value: true }, { value: false }];
  }

  if (props.getFieldFacets) {
    fieldFacets = props.getFieldFacets({ runStats: props.stats });
  }

  const isDisabled = !!props.field.disabled?.({ runStats: props.stats });

  const [state, setState] = useState('');

  const fieldFacetsWithCustom = fieldFacets.slice(0);
  if (isMultiple) {
    const valueArray = [...(Array.isArray(props.value) ? props.value : [])];
    for (const item of valueArray) {
      if (
        typeof item !== 'undefined' &&
        !fieldFacets.some((i) => i.value === item)
      ) {
        fieldFacetsWithCustom.push({ value: item });
      }
    }
  }

  const uniqueValues = new Set();
  const filteredFacets = fieldFacetsWithCustom
    .filter((facet) => {
      if (!state) return true;

      const haystack = `${facet.value}`.trim().toLowerCase();
      const needle = `${state}`.trim().toLowerCase();
      return haystack.includes(needle);
    })
    .filter((item) => {
      if (!uniqueValues.has(item.value)) {
        uniqueValues.add(item.value);
        return true;
      }
      return false;
    });

  function onChange(value: BasicValueType | null) {
    if (isReadOnly) return;

    if (isMultiple) {
      if (value == null) return;

      const arr = Array.isArray(props.value)
        ? props.value
        : props.value != null
        ? [props.value]
        : [];

      const idx = arr.indexOf(value);
      if (idx === -1) {
        props.onChange([...arr, value]);
      } else {
        arr.splice(idx, 1);
        props.onChange(arr.length > 0 ? arr : undefined);
      }
    } else {
      props.onChange(value);
      setOpen(false);
    }
  }

  useEffect(() => {
    if (typeof props.value !== 'undefined' || isReadOnly) return;
    const timer = window.setTimeout(() => setOpen(true));
    return () => window.clearTimeout(timer);
  }, [props.value, isReadOnly]);

  if (valueType === 'number') {
    let value =
      typeof props.value !== 'undefined'
        ? typeof props.value === 'string'
          ? props.value
          : `${props.value}`
        : '';

    if (props.field.suffix && value.endsWith(props.field.suffix)) {
      value = value.slice(0, -props.field.suffix.length);
    }

    return (
      <VariableInputForm
        placeholder={
          props.field.valueType === 'boolean'
            ? 'true or false'
            : props.field.valueType === 'number'
            ? '10'
            : props.field.label
        }
        value={value}
        onChange={(value) => {
          if (isReadOnly) return;
          const number = Number(value);
          if (!Number.isNaN(number)) {
            onChange(number);
          } else {
            onChange(value);
          }
        }}
        endAdornment={props.field.suffix}
        updateEveryChange={props.updateEveryChange}
        readOnly={isReadOnly}
      />
    );
  }

  const textToDisplay =
    typeof props.value !== 'undefined'
      ? typeof props.value === 'string'
        ? props.value
        : Array.isArray(props.value)
        ? props.value.join(', ')
        : `${props.value}`
      : props.field.valueType === 'boolean'
      ? 'true or false'
      : props.field.label;

  return (
    <Popover
      open={open && !isReadOnly}
      onOpenChange={(newOpen) => {
        if (isReadOnly) return;
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger
        className={cn(
          'flex items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-1',
          'border-none bg-tertiary hover:bg-quaternary',
          { 'text-tertiary': typeof props.value === 'undefined' },
          { 'opacity-50': isDisabled },
          {
            'hover:bg-tertiary': isReadOnly,
          }
        )}
        disabled={isDisabled || isReadOnly}
      >
        <DisabledTooltip
          disabled={isDisabled}
          disabledMsg={props.field.disabledTooltip}
        >
          {`${textToDisplay.slice(0, 30)}${
            textToDisplay.length > 30 ? '...' : ''
          }`}
        </DisabledTooltip>
        {!isReadOnly && <ChevronDownIcon className="h-3 w-3 text-tertiary" />}
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent
          className="max-h-[500px] overflow-auto p-0 text-xs"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Value..."
              className="m-2 h-8 p-2 text-xs"
              value={state ?? ''}
              inputMode={
                props.field?.valueType === 'number' ? 'numeric' : undefined
              }
              onValueChange={(value) => {
                if (isReadOnly) return;
                setState(value);
              }}
            />

            {!state && fieldFacets.length === 0 ? (
              <div className="w-full py-7 pb-8 text-center">Enter a value</div>
            ) : null}

            <CommandList>
              {state && props.field.valueType !== 'boolean' && (
                <CommandGroup
                  className={cn(
                    'm-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-items]]:m-1',
                    {
                      hidden: !state,
                    }
                  )}
                  heading={
                    <span
                      className={
                        'px-1 text-xxs font-medium uppercase tracking-wide text-tertiary'
                      }
                    >
                      Custom value
                    </span>
                  }
                >
                  <CustomValue key={state} value={state} onChange={onChange} />
                </CommandGroup>
              )}

              {filteredFacets.length > 0 && (
                <CommandGroup
                  className="m-0 [&_[cmdk-group-heading]]:bg-secondary [&_[cmdk-group-items]]:m-1"
                  heading={
                    <span
                      className={
                        'px-1 text-xxs font-medium uppercase tracking-wide text-tertiary'
                      }
                    >
                      Top values
                    </span>
                  }
                >
                  {filteredFacets.map((facet, idx) => (
                    <CommandItem
                      key={idx}
                      className={
                        'flex items-center justify-between gap-2 px-2 py-1 text-xxs'
                      }
                      onSelect={() => onChange(facet.value)}
                    >
                      <TextOverflowTooltip>
                        {typeof facet.value === 'string'
                          ? facet.value
                          : `${facet.value}`}
                      </TextOverflowTooltip>
                      {facet.value === props.value ||
                      (Array.isArray(props.value) &&
                        props.value.includes(facet.value)) ? (
                        <CheckIcon className="h-4 w-4 flex-shrink-0" />
                      ) : isMultiple ? (
                        <span className="h-4 w-4 flex-shrink-0" />
                      ) : null}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}
